import baseDayjs from 'dayjs';
import calendarPlugin from 'dayjs/plugin/calendar';
import isSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';

baseDayjs.extend(utc);
baseDayjs.extend(calendarPlugin);
baseDayjs.extend(isSameOrBeforePlugin);

/**
 * All times are in the provider's local timezone, which is what we want to display.
 * Therefore, remove the timezone so that the startTime will be parsed and formatted
 * in UTC, preventing any accidental changes in the displayed time.
 */
const dayjs = (date) => {
    const removedTz = __removeTimezone(date);

    /**
     * If anyone uses the dayjs objectSupport plugin, this will break unless we use this workaround.
     * Even if the plugin is used by an entirely unrelated dependency, it can still cause this to break.
     * The dayjs plugins have side effects that change some global functions, so any dependency using that plugin
     * within the same app as a dependency using the utc plugin, will break. The breaks are very difficult to find in CI.
     * https://github.com/iamkun/dayjs/issues/1530
     */
    if (baseDayjs.isDayjs(removedTz)) {
        return baseDayjs.utc(removedTz.format());
    } else {
        return baseDayjs.utc(removedTz);
    }
};

const CALENDAR_FORMATS = {
    sameDay: '[Today], MMM D',
    nextDay: '[Tomorrow], MMM D',
    nextWeek: 'ddd, MMM D',
    lastDay: 'ddd, MMM D',
    lastWeek: 'ddd, MMM D',
    sameElse: 'ddd, MMM D',
};

const TIMESTONE_NEXT_AVAILABLE_CALENDAR_FORMATS = {
    sameDay: '[today], MMM D',
    nextDay: 'ddd, MMM D',
    nextWeek: 'ddd, MMM D',
    lastDay: 'ddd, MMM D',
    lastWeek: 'ddd, MMM D',
    sameElse: 'ddd, MMM D',
};

const TIMESTONE_ONE_WEEK_CALENDAR_FORMATS = {
    sameDay: 'MMM D',
    nextDay: 'MMM D',
    nextWeek: 'MMM D',
    lastDay: 'MMM D',
    lastWeek: 'MMM D',
    sameElse: 'MMM D',
};

/**
 * Returns true if the input is a date
 * @param {*} date
 */
export function isDate(date) {
    const isValidType = typeof date === 'string' || typeof date === 'object';
    return Boolean(date && isValidType && dayjs(date).isValid());
}

export function addDays(date, value) {
    return dayjs(date).add(value, 'day');
}

export function toTicks(date) {
    return dayjs(date).unix() * 10000000 + 621355968000000000;
}

/**
 *
 * @param {*} date ISO date string or other date object accepted by dayjs
 * @return {string} The date formatted to YYYY-MM-DD with no time or timezone
 */
export function formatDate(date) {
    return dayjs(date).format('YYYY-MM-DD');
}

/**
 *
 * @param {string} referenceDate
 * @param {string} otherDate
 * @return {boolean}
 */
export function isSameOrBefore(date, referenceDate) {
    return dayjs(date).isSameOrBefore(dayjs(referenceDate));
}

/**
 *
 * @param {string} referenceDate
 * @param {string} otherDate
 * @return {boolean}
 */
export function isBefore(date, referenceDate) {
    return dayjs(date).isBefore(dayjs(referenceDate));
}

/**
 * @param {string} startDate
 * @param {number} num
 * @returns {[string]} An array of date strings in `YYYY-MM-DD` format
 */
export function getDateRange(startDate, count) {
    if (!startDate || !count) return [];

    const referenceDate = dayjs(startDate);
    const dates = [];

    for (let i = 0; i < count; ++i) {
        dates.push(referenceDate.add(i, 'day').format('YYYY-MM-DD'));
    }

    return dates;
}

export function formatDesktopControlsDayOfWeek(date) {
    return dayjs(date).format('ddd');
}

export function formatDesktopControlsDate(date) {
    return dayjs(date).format('MMM D');
}

export function formatMobileControlsDate(date, today) {
    if (!date || !today) return null;

    return dayjs(date).calendar(dayjs(today), CALENDAR_FORMATS);
}

export function formatNextAvailableMobileControlsDate(date, today) {
    if (!date || !today) return null;

    return `From ${dayjs(date).calendar(
        dayjs(today),
        TIMESTONE_NEXT_AVAILABLE_CALENDAR_FORMATS
    )}`;
}

export function formatOneWeekMobileControlsDate(date, today) {
    if (!date || !today) return null;

    return dayjs(date).calendar(
        dayjs(today),
        TIMESTONE_ONE_WEEK_CALENDAR_FORMATS
    );
}

export function formatNextAvailability(startTime) {
    return dayjs(startTime).format('ddd, MMM D');
}

export function formatTimeslot(startTime) {
    return dayjs(startTime).format('h:mm a');
}

export function formatGridAriaLabel(date) {
    return dayjs(date).format('MMMM D, YYYY');
}

export function formatTimeslotAriaLabel(startTime) {
    return dayjs(startTime).format('h:mm A [on] dddd, MMMM D, YYYY');
}

export function isToday(today, date) {
    return dayjs(today).isSame(dayjs(date));
}

export function isTomorrow(today, date) {
    return dayjs(today).add(1, 'day').isSame(dayjs(date));
}

export function formatDayOfWeek(today, startTime) {
    const date = formatDate(startTime);
    if (isToday(today, date)) {
        return 'Today';
    } else if (isTomorrow(today, date)) {
        return 'Tomorrow';
    } else {
        return formatDesktopControlsDayOfWeek(startTime);
    }
}

export function parseTimezone(startTime) {
    const matches = startTime.match(/(Z|[-+]\d{2}:?\d{2})/);
    if (!matches || !matches[0]) return null;
    // Z is +00:00
    if (matches[0] === 'Z') return '+00:00';
    return matches[0];
}

export function formatDesktopControlsFullDayOfWeek(date, today) {
    if (isToday(today, date)) {
        return 'Today';
    } else if (isTomorrow(today, date)) {
        return 'Tomorrow';
    } else return dayjs(date).format('dddd');
}

export function formatDesktopControlsDayOfMonth(date) {
    return dayjs(date).format('D');
}

export function formatDesktopControlsMonth(date) {
    return dayjs(date).format('MMM');
}
/**
 * Removes the timezone offset from an ISO string. Exported only for unit testing.
 * @param {object|string} startTime
 * @returns {object|string}
 */
export function __removeTimezone(startTime) {
    if (typeof startTime !== 'string') return startTime;

    // Parse the non-timezone portion of the ISO string
    const matches = startTime.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/);

    if (!matches) return startTime;

    return matches[0];
}
